<template>
    <flex-element :element-type="elementType">
        <div
            ref="mapContainer"
            :class="[containerClasses]"
            :style="style"
            style="width: 100%;"
        />
    </flex-element>
</template>

<script>
import { mapState } from 'vuex';

import googleApiUtilsMixin from '@/mixins/google-api-utils';
import baseOrganism from '@/mixins/base-organism/base-organism-dumb';
import { markerIcon } from '@/utils/map-marker';
import listenOnRootMixin from '@/mixins/listen-on-root';
import mapStyles from '@/data/google-map-styles/paper.json';
import { getClassDefinition } from '@/utils/tailwind-helper';

export default {
    mixins: [baseOrganism, listenOnRootMixin, googleApiUtilsMixin],

    props: {
        isVisible: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            map: null,
            markers: [],
            defaultCoordinates: {
                lat: 33.65,
                lng: -117.75,
            },
            useBrowser: false,
            locations: [],
            styles: mapStyles,
            containerClassGroups: ['height'],
        };
    },

    computed: {
        ...mapState({
            settings: state => state.settings,
        }),

        containerClasses() {
            return this.htmlClass.filter((className) => {
                const classDefinition = getClassDefinition(className);
                const { tailwindDefinitionProp } = classDefinition;

                return this.containerClassGroups.includes(tailwindDefinitionProp);
            });
        },

        isReady() {
            return window.google && this.map;
        },

        style() {
            return !this.containerClasses || this.containerClasses.length === 0 ? 'height: 400px; width: 100%' : '';
        },
    },

    watch: {
        locations() {
            this.loadLocations();
        },
    },

    created() {
        this.listenOnRoot('flex::collection::search-data', this.onSearchDataUpdated);
        this.listenOnRoot('flex::google-map::showOnMap', this.showOnMap);
    },

    mounted() {
        this.initializeMap();
    },

    methods: {
        async onSearchDataUpdated(val) {
            if (val) {
                this.locations = val.results.items.filter(i => i.locations).map(i => i.locations).flat();
            }
        },

        async initializeMap() {
            await this.loadGoogleMapsApi();

            try {
                if (window.google && !this.map) {
                    const mapElement = this.$refs.mapContainer;

                    if (mapElement) {
                        const mapOptions = {
                            center: this.coordinates || this.defaultCoordinates,
                            zoom: 6,
                            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
                            styles: this.styles,
                            maxZoom: 16,
                        };
                        this.map = new window.google.maps.Map(mapElement, mapOptions);
                    }

                    window.google.maps.visualRefresh = true;
                }
            } catch (e) {
                console.log(e);
            }
        },

        async loadLocations() {
            this.clearOldMarkers();
            if (this.locations) {
                this.loadMarkers();
            }
        },

        showOnMap(val) {
            if (window.google && this.locations && this.markers) {
                const item = this.locations.find(i => i.coordinates === val);
                const marker = this.markers.find(m => `${ m.position.lat() },${ m.position.lng() }` === val);
                if (item && marker) {
                    const infoWindow = this.createInfoWindow(item);
                    infoWindow.open(this.map, marker);
                    infoWindow.setPosition(marker.getPosition());
                    this.map.setCenter(marker.getPosition());
                    this.map.setZoom(16);
                } else if (this.map) {
                    this.map.setCenter(val);
                }
            }
        },

        async loadMarkers() {
            if (window.google && this.map) {
                const bounds = new window.google.maps.LatLngBounds();

                if (this.locations) {
                    Array.prototype.forEach.call(this.locations, (item) => {
                        const coordinates = item.coordinates.split(',');
                        const latlng = new window.google.maps.LatLng(coordinates[0], coordinates[1]);
                        const marker = new window.google.maps.Marker({
                            position: latlng,
                            map: this.map,
                            title: item.locationName,
                            icon: {
                                url: markerIcon,
                                scaledSize: new window.google.maps.Size(20, 30),
                            },
                            optimized: false,
                        });

                        const infowindow = this.createInfoWindow(item);

                        window.google.maps.event.addListener(marker, 'click', () => {
                            infowindow.open(this.map, marker);
                        });

                        this.markers.push(marker);
                        bounds.extend(latlng);
                    });

                    if (this.markers && this.markers.length > 0 && this.map) {
                        this.map.fitBounds(bounds);
                    }
                }
            }
        },

        createInfoWindow(item) {
            let locationInfo = `<div><strong>${ item.locationName }</strong></div>`;
            if (item.address || item.city || item.state || item.zip) {
                locationInfo += `<div>${ item.address }</div>`;
                if (item.city || item.state || item.zip) {
                    locationInfo += '<div>';
                    if (item.city) {
                        locationInfo += `${ item.city }`;
                    }
                    if (item.state) {
                        locationInfo += `, ${ item.state }`;
                    }
                    if (item.zip) {
                        locationInfo += ` ${ item.zip }`;
                    }
                    locationInfo += '</div>';
                }
            }
            if (item.phone) {
                locationInfo += `<div>Phone: ${ item.phone }</div>`;
            }
            if (item.email) {
                locationInfo += `<div>Email: ${ item.email }</div>`;
            }
            locationInfo += `<div><a href="${ this.locationMapUrl(item) }" target="_blank">View in Google Maps</a></div>`;

            const infoWindow = new window.google.maps.InfoWindow({
                content: locationInfo,
            });

            return infoWindow;
        },

        locationMapUrl(location) {
            return `http://maps.google.com/maps?q=${ location.locationName } ${ location.address } ${ location.city },${ location.state } ${ location.zip }`;
        },

        clearOldMarkers() {
            for (let i = 0; i < this.markers.length; i++) {
                this.markers[i].setMap(null);
            }
            this.markers = [];
        },
    },
};
</script>
<style lang="scss">
.map-google {
    z-index: 9100;

    &__tab {
        background-color: white;
        color: gray;
        margin-right: 8px;
        font-size: 14px;
        padding: 8px 20px;
        border-radius: 8px 8px 0 0;
        opacity: 0.75;
        transition: all 0.35s;

        &.#{ $is-selected } {
            margin-bottom: -2px;
            opacity: 1;
            background-color: var(--form-buttonColor);
            color: black;
        }
    }

    &__list-item {
        margin: 10px 0 10px 0;

        h2 {
            font-weight: bold;
        }

        a {
            font-size: smaller;
        }
    }
}
</style>
